import { ElementRef, PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, styles } from '@frontend/design-system';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { PROVIDER_COLUMN_GAP, SCROLLABLE_BUTTON_WIDTH } from '../constants';
import { useCalendarView } from '../providers';

type ScrollableButtonProps = {
  containerRef: React.RefObject<ElementRef<'article'>>;
  disabled?: boolean;
  direction: 'left' | 'right';
  isCircularButton?: boolean;
};

const ScrollableButton = ({ containerRef, disabled, direction, isCircularButton = false }: ScrollableButtonProps) => {
  const isLeft = direction === 'left';

  const handleScroll = () => {
    if (containerRef.current === null) return;
    // Remove the width of the scrollable buttons from the scroll amount
    const scrollAmount = containerRef.current.clientWidth - SCROLLABLE_BUTTON_WIDTH * 2;
    containerRef.current.scrollTo({
      left: containerRef.current.scrollLeft + (isLeft ? -scrollAmount : scrollAmount),
      behavior: 'smooth',
    });
  };

  return (
    <div css={[scrollButtonStyle.container, isCircularButton && scrollButtonStyle.circularContainer(isLeft)]}>
      <NakedButton
        css={[styles.flexCenter, scrollButtonStyle.button(isCircularButton)]}
        disabled={disabled}
        onClick={handleScroll}
        trackingId={`${
          isCircularButton ? CALENDAR_TRACKING_IDS.scrollEventsButton : CALENDAR_TRACKING_IDS.scrollProvidersButton
        }:${direction}`}
      >
        <Icon name={isLeft ? 'caret-left' : 'caret-right'} color={disabled ? 'disabled' : 'subdued'} />
      </NakedButton>
    </div>
  );
};

export const CalendarScrollButtons = ({ containerRef }: { containerRef: React.RefObject<ElementRef<'article'>> }) => {
  const {
    containerState: { isLeft, isRight },
  } = useCalendarView(['containerState']);

  return (
    <>
      {!isLeft && <ScrollableButton isCircularButton direction='left' containerRef={containerRef} />}
      {!isRight && <ScrollableButton isCircularButton direction='right' containerRef={containerRef} />}
    </>
  );
};

export const ProviderScrollWrapper = ({
  children,
  containerRef,
}: PropsWithChildren<{ containerRef: React.RefObject<ElementRef<'article'>> }>) => {
  const {
    containerState: { isLeft, isRight, isScrollable },
  } = useCalendarView(['containerState']);

  return (
    <motion.section css={{ overflowY: 'clip', gridColumn: '1 / -1', display: 'flex', gap: `${PROVIDER_COLUMN_GAP}px` }}>
      <div css={[providerScrollButtonContainerStyle, { left: 0 }]}>
        {isScrollable && <ScrollableButton disabled={isLeft} direction='left' containerRef={containerRef} />}
      </div>
      {children}
      <div css={[providerScrollButtonContainerStyle, { right: 0 }]}>
        {isScrollable && <ScrollableButton disabled={isRight} direction='right' containerRef={containerRef} />}
      </div>
    </motion.section>
  );
};

const providerScrollButtonContainerStyle = css({
  position: 'sticky',
  top: 0,
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  background: theme.colors.white,
  zIndex: theme.zIndex.low,
  width: SCROLLABLE_BUTTON_WIDTH,
});

const scrollButtonStyle = {
  container: css({
    top: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: SCROLLABLE_BUTTON_WIDTH,
    background: theme.colors.white,
  }),
  circularContainer: (isLeft: boolean) =>
    css({
      position: 'absolute',
      top: 'calc(50% + 50px)',
      right: isLeft ? 'auto' : 0,
      opacity: 0.8,
      borderRadius: theme.borderRadius.full,
      background: theme.colors.neutral10,
      border: `1px solid ${theme.colors.neutral20}`,
      height: 44,
      width: 44,
      margin: theme.spacing(2),
      transition: 'all 0.3s ease',
      ':hover': {
        opacity: 0.9,
        boxShadow: theme.shadows.light,
        background: theme.colors.white,
        border: `1px solid ${theme.colors.primary50}`,
      },
    }),
  button: (isCircularButton: boolean) =>
    css({
      height: '100%',
      width: '100%',
      transition: 'background 0.3s ease',
      borderRadius: isCircularButton ? theme.borderRadius.full : undefined,
      boxShadow: !isCircularButton ? theme.shadows.light : 'none',
      ':hover:not(:disabled)': {
        background: theme.colors.neutral5,
      },
      ':disabled': {
        cursor: 'not-allowed',
        boxShadow: 'none',
      },
    }),
};
